import React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

export const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50 to-amber-50 dark:from-gray-900 dark:via-purple-950 dark:to-gray-900 z-[1]"
      data-testid="hero-section"
    >
      {/* Animated background elements - responsive sizes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-40 sm:w-56 md:w-72 h-40 sm:h-56 md:h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-20 sm:top-40 right-5 sm:right-10 w-40 sm:w-56 md:w-72 h-40 sm:h-56 md:h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-10 sm:bottom-20 left-1/2 w-40 sm:w-56 md:w-72 h-40 sm:h-56 md:h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32 pt-24 sm:pt-28 md:pt-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg mb-6 sm:mb-8 border border-purple-100 dark:border-purple-900">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              AI-Powered Solutions for Modern Businesses
            </span>
          </div>

          {/* Main Heading - Responsive text sizes */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 leading-tight px-2">
            Building Modern Solutions
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 dark:from-purple-400 dark:via-orange-400 dark:to-yellow-400">
              with Rare Creativity
            </span>
          </h1>

          {/* Subheading - Responsive */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-8 sm:mb-10 md:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
            Unlock your business's full potential with cutting-edge AI
            solutions. We design and build exceptional mobile apps, web
            platforms, and IoT systems.
          </p>

          {/* CTA Buttons - Responsive */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 md:mb-16 px-4">
            <Button
              onClick={() => scrollToSection("contact")}
              size="lg"
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
              data-testid="hero-get-started"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button
              onClick={() => scrollToSection("services")}
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-gray-300 dark:border-gray-600 hover:border-purple-600 dark:hover:border-purple-400 text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm transition-all hover:scale-105"
              data-testid="hero-explore-services"
            >
              Explore Services
            </Button>
          </div>

          {/* Stats - Responsive */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-xs sm:max-w-md md:max-w-2xl mx-auto px-4">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-purple-600 dark:text-purple-400 mb-1 sm:mb-2">
                50+
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Projects Delivered
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-500 dark:text-orange-400 mb-1 sm:mb-2">
                99%
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Client Satisfaction
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-1 sm:mb-2">
                24/7
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Support Available
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
